import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, message, Modal, Row } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CountryInput from '@/components/FormItems/CountryInput';
import RegionInput from '@/components/FormItems/RegionInput';
import { clearAllCitiesCache } from '@/hooks/data/masterDataHook';
import { CityModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
const PAGE_TITLE = 'cityPage_Title';

const columns: IColumnType<CityModel>[] = [
  {
    title: 'cityPage_Name',
    dataIndex: 'cityName',
    sorter: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'cityPage_Code',
    dataIndex: 'cityCode',
  },
  {
    title: 'cityPage_Region',
    dataIndex: 'regionName',
  },
  {
    title: 'cityPage_Country',
    dataIndex: 'countryName',
  },
];

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'cityName', label: 'cityPage_Name' }];

const Regions = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const clearCache = () => {
    clearAllCitiesCache();
  };
  const createCountry = values => {
    setLoading(true);
    apiSdk.CityService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateCountry = values => {
    setLoading(true);
    apiSdk.CityService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const countryId = Form.useWatch('countryId', form);

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <Button
          key="create"
          type="primary"
          className="nta-create-btn"
          icon={<PlusCircleOutlined />}
          onClick={() => {
            form.resetFields();
            setIsNewForm(true);
            setFormVisible(true);
          }}
        >
          {t('createNew')}
        </Button>,
      ]}
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/commons/cities/get-cities"
          bordered
          order_by="cityName"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
              continent: `${record.continent}`,
            });
          }}
          allowDelete={true}
          delete_url="/commons/cities/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        ></ZTable>
        <Modal
          visible={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('regionPage_CreateCity') : t('regionPage_UpdateCity')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateCountry(values);
                } else {
                  createCountry(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="vertical">
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('cityPage_Name')} name="cityName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('cityPage_Code')} name="cityCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('cityPage_Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <CountryInput
                    onChange={() => {
                      form.setFieldValue('regionId', null);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('cityPage_Region')} name="regionId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <RegionInput countryId={countryId} disabled={!countryId} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </MainContent>
  );
};
export default Regions;
